/**
 * Sign-up and results-email helpers (19 §4.11–4.12). Pure functions, tested in test/signup.test.ts.
 * The server validates everything again (apps/api/src/signup.ts); these rules match it so errors show before sending.
 */

export const COMPANY_SIZES = ["Just me", "2–10 people", "11–50", "51–100", "Over 100"] as const;

export const NAME_MAX = 80;
export const COMPANY_MAX = 120;

export interface SignupFields { name: string; company: string; companySize: string; email: string; phone: string }
export type SignupErrors = Partial<Record<keyof SignupFields, string>>;

export function isValidEmail(s: string): boolean {
  const t = s.trim();
  return t.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

/** Loose: digits, spaces, +, brackets and dashes, with 7–20 digits. */
export function isValidPhone(s: string): boolean {
  const t = s.trim();
  if (!/^[0-9 +()\-]+$/.test(t)) return false;
  const digits = t.replace(/\D/g, "").length;
  return digits >= 7 && digits <= 20;
}

/** Field errors in words, in form order. Empty object = fine to send. */
export function validateSignup(f: SignupFields): SignupErrors {
  const e: SignupErrors = {};
  const name = f.name.trim();
  const company = f.company.trim();
  if (!name) e.name = "Please tell us your name.";
  else if (name.length > NAME_MAX) e.name = `Your name must be ${NAME_MAX} characters or fewer.`;
  if (!company) e.company = "Please tell us the company you work for.";
  else if (company.length > COMPANY_MAX) e.company = `The company name must be ${COMPANY_MAX} characters or fewer.`;
  if (f.companySize && !(COMPANY_SIZES as readonly string[]).includes(f.companySize)) e.companySize = "Choose a company size from the list.";
  if (f.email.trim() && !isValidEmail(f.email)) e.email = "That doesn't look like an email address. Check it, or leave it blank.";
  if (f.phone.trim() && !isValidPhone(f.phone)) e.phone = "Use digits, spaces, +, brackets or dashes (7 to 20 digits), or leave it blank.";
  return e;
}

/** The body for POST /api/auth/signup: trimmed, blanks left out. */
export function signupPayload(f: SignupFields, code: string | null): Record<string, string> {
  const out: Record<string, string> = { name: f.name.trim(), company: f.company.trim() };
  for (const k of ["companySize", "email", "phone"] as const) if (f[k].trim()) out[k] = f[k].trim();
  if (code) out.code = code;
  return out;
}

/** A cohort code from the link: ?code=X, or #/?code=X. Null when there is none. */
export function cohortCodeFromUrl(search: string, hash: string): string | null {
  const fromSearch = new URLSearchParams(search).get("code");
  const q = hash.indexOf("?");
  const fromHash = q >= 0 ? new URLSearchParams(hash.slice(q + 1)).get("code") : null;
  const code = (fromSearch ?? fromHash ?? "").trim();
  return code ? code.slice(0, 64) : null;
}

/** The same address without the cohort code, so it is not carried into the game or bookmarked. */
export function urlWithoutCode(pathname: string, search: string, hash: string): string {
  const s = new URLSearchParams(search);
  s.delete("code");
  let h = hash;
  const q = hash.indexOf("?");
  if (q >= 0) {
    const hs = new URLSearchParams(hash.slice(q + 1));
    hs.delete("code");
    const rest = hs.toString();
    h = hash.slice(0, q) + (rest ? `?${rest}` : "");
    if (h === "#/" || h === "#") h = "";
  }
  const qs = s.toString();
  return `${pathname}${qs ? `?${qs}` : ""}${h}`;
}

/** What the outcome panel says after Send. Never claims an email was sent when it was only saved. */
export function resultsEmailMessage(status: "sent" | "queued", email: string): string {
  return status === "sent"
    ? `Sent. Your results are on their way to ${email}.`
    : `We've saved your request and will send your results to ${email} shortly.`;
}
